import {
  readTraceEvent,
  type RuntimePosition,
  type TraceEvent,
  type TraceEventOf,
} from "@/lib/previewDecisions";

/**
 * The Conversation tab's pure parts: which events are the conversation, and
 * where the runtime is in it. Nothing here reads a body — text arrives through
 * the event-detail read, one turn at a time, on the clinician's say-so.
 */

export type ConversationTurn = TraceEventOf<"turn">;

/** What a run page's event list holds, read once: the events this build can place, and how many it could not. */
export type ReadEvents = {
  events: TraceEvent[];
  unreadable: number;
};

export function readEvents(records: readonly unknown[]): ReadEvents {
  const events: TraceEvent[] = [];
  let unreadable = 0;
  for (const record of records) {
    const reading = readTraceEvent(record);
    if (reading.kind === "event") {
      events.push(reading.event);
    } else {
      unreadable += 1;
    }
  }
  return { events, unreadable };
}

/** The run's `turn` events in the order the runtime recorded them. */
export function conversationTurns(
  events: readonly TraceEvent[],
): ConversationTurn[] {
  return events
    .filter((event): event is ConversationTurn => event.family === "turn")
    .sort((a, b) => a.sequence - b.sequence);
}

/**
 * Where the conversation is now: the position AFTER the latest turn, which is
 * the producer's own account of the agent, track, phase and step the runtime
 * moved to. Null before any turn has been recorded.
 */
export function currentPosition(
  turns: readonly ConversationTurn[],
): RuntimePosition | null {
  const last = turns[turns.length - 1];
  return last ? last.detail.after : null;
}
